import React, { useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Helper to determine metallic gradient based on filament name or fallback
 */
export const getMetallicStyle = (filamentName) => {
  const name = (filamentName || '').toLowerCase();
  if (name.includes('silver') || name.includes('white') || name.includes('platinum')) {
    return {
      rimGradient: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 45%, #94a3b8 100%)',
      borderColor: '#94a3b8',
      glowColor: 'rgba(203, 213, 225, 0.4)',
      sheen: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.05) 50%, transparent 100%)'
    };
  }
  if (name.includes('copper') || name.includes('orange') || name.includes('rust')) {
    return {
      rimGradient: 'linear-gradient(135deg, #fed7aa 0%, #ea580c 45%, #9a3412 100%)',
      borderColor: '#c2410c',
      glowColor: 'rgba(234, 88, 12, 0.4)',
      sheen: 'linear-gradient(135deg, rgba(254,215,170,0.6) 0%, rgba(254,215,170,0.05) 50%, transparent 100%)'
    };
  }
  if (name.includes('bronze') || name.includes('brass')) {
    return {
      rimGradient: 'linear-gradient(135deg, #fef3c7 0%, #ca8a04 45%, #713f12 100%)',
      borderColor: '#a16207',
      glowColor: 'rgba(202, 138, 4, 0.4)',
      sheen: 'linear-gradient(135deg, rgba(254,243,199,0.6) 0%, rgba(254,243,199,0.05) 50%, transparent 100%)'
    };
  }
  if (name.includes('black') || name.includes('obsidian') || name.includes('charcoal')) {
    return {
      rimGradient: 'linear-gradient(135deg, #64748b 0%, #1e293b 45%, #020617 100%)',
      borderColor: '#d4af37',
      glowColor: 'rgba(212, 175, 55, 0.4)',
      sheen: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 60%)'
    };
  }
  // Default Rich Gold
  return {
    rimGradient: 'linear-gradient(135deg, #fef08a 0%, #eab308 40%, #ca8a04 70%, #854d0e 100%)',
    borderColor: '#eab308',
    glowColor: 'rgba(234, 179, 8, 0.4)',
    sheen: 'linear-gradient(135deg, rgba(254,240,138,0.7) 0%, rgba(254,240,138,0.08) 50%, transparent 100%)'
  };
};

/**
 * PackageArtShowcase Component
 * Dynamically displays predefined artwork for physical coins (front/back overlapped)
 * and trophy cards (front/back fanned) in a slick 3D stage with hover depth effects.
 */
export default function PackageArtShowcase({
  pkg,
  availableSystemCoins = [],
  allPhysicalCoins = [],
  allDesigns = []
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [showInspectModal, setShowInspectModal] = useState(false);

  // 1. Resolve Coin Art
  const isBlankCoin = pkg?.coin_template_mode === 'blank';

  const sysCoin = pkg?.system_coin || 
    (pkg?.system_coin_id ? (availableSystemCoins.find(c => c.id === pkg.system_coin_id) || allPhysicalCoins.find(c => c.id === pkg.system_coin_id)) : null);

  const headsDesign = sysCoin ? allDesigns.find(d => d.id === sysCoin.face_1_design_id) : null;
  const tailsDesign = sysCoin ? allDesigns.find(d => d.id === sysCoin.face_2_design_id) : null;

  const headsImg = headsDesign?.config?.previewImage || 
    headsDesign?.config?.imageUrl || 
    headsDesign?.config?.preview_image || 
    headsDesign?.config?.photoUrl || 
    sysCoin?.preview_file || 
    null;

  const tailsImg = tailsDesign?.config?.previewImage || 
    tailsDesign?.config?.imageUrl || 
    tailsDesign?.config?.preview_image || 
    tailsDesign?.config?.photoUrl || 
    null;

  const hasCoinArt = Boolean(headsImg || tailsImg);
  const coinMetal = getMetallicStyle(sysCoin?.body_filament);

  // 2. Resolve Card Art
  const isBlankCard = pkg?.card_template_mode === 'blank';

  const cardDesign = pkg?.trophy_card_template || 
    (pkg?.trophy_card_template_id ? allDesigns.find(d => d.id === pkg.trophy_card_template_id) : null);

  const cardFrontImg = cardDesign?.config?.preview_image || 
    cardDesign?.config?.previewImage || 
    cardDesign?.config?.imageUrl || 
    null;

  const cardBackImg = cardDesign?.config?.preview_image_back || 
    cardDesign?.config?.previewImageBack || 
    null;

  const hasCardArt = Boolean(cardFrontImg || cardBackImg);

  // If package has neither predefined art nor blank modes, don't show stage
  const isLibraryCoin = pkg?.coin_template_mode === 'library';
  const isLibraryCard = pkg?.card_template_mode === 'library';
  const hasPredefinedArt = hasCoinArt || hasCardArt;
  const hasBlueprintMode = isBlankCoin || isBlankCard;
  const hasLibraryMode = (isLibraryCoin && !hasCoinArt) || (isLibraryCard && !hasCardArt);

  if (!hasPredefinedArt && !hasBlueprintMode && !hasLibraryMode) {
    return null;
  }

  const isBundle = hasCoinArt && hasCardArt;

  return (
    <>
      <div
        data-testid="package-art-showcase"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          if (hasPredefinedArt) {
            e.stopPropagation();
            setShowInspectModal(true);
          }
        }}
        style={{
          position: 'relative',
          height: isBundle ? '220px' : (hasCardArt ? '210px' : (hasCoinArt ? '190px' : (hasLibraryMode ? '148px' : '140px'))),
          margin: '-20px -20px 16px -20px',
          borderRadius: '12px 12px 0 0',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'radial-gradient(ellipse at 50% 60%, rgba(30, 41, 59, 0.85) 0%, rgba(15, 23, 42, 0.98) 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          cursor: hasPredefinedArt ? 'pointer' : 'default',
          userSelect: 'none',
          perspective: '1000px'
        }}
        title={hasPredefinedArt ? 'Click to inspect full-size front & back artwork' : ''}
      >
        {/* Dynamic Atmospheric Glow & Vignette */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: isBundle
            ? 'radial-gradient(circle at 50% 40%, rgba(56, 189, 248, 0.22) 0%, rgba(234, 179, 8, 0.15) 50%, transparent 80%)'
            : (hasCardArt 
                ? 'radial-gradient(circle at 50% 40%, rgba(56, 189, 248, 0.25) 0%, rgba(99, 102, 241, 0.15) 60%, transparent 80%)'
                : (hasCoinArt 
                    ? 'radial-gradient(circle at 50% 40%, rgba(234, 179, 8, 0.25) 0%, rgba(202, 138, 4, 0.12) 60%, transparent 80%)'
                    : (hasLibraryMode
                        ? 'radial-gradient(circle at 50% 40%, rgba(46, 160, 67, 0.25) 0%, rgba(16, 185, 129, 0.12) 60%, transparent 80%)'
                        : 'radial-gradient(circle at 50% 40%, rgba(148, 163, 184, 0.15) 0%, transparent 70%)'))),
          opacity: isHovered ? 1 : 0.75,
          transition: 'opacity 0.35s ease',
          pointerEvents: 'none'
        }} />

        {/* Subtle Geometric Background Rings */}
        <div style={{
          position: 'absolute',
          width: '280px',
          height: '280px',
          borderRadius: '50%',
          border: '1px dashed rgba(255, 255, 255, 0.06)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          width: '190px',
          height: '190px',
          borderRadius: '50%',
          border: '1px solid rgba(255, 255, 255, 0.04)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none'
        }} />

        {/* Badges in Top Corners */}
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          zIndex: 30
        }}>
          {hasCoinArt && (
            <span style={{
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '12px',
              backgroundColor: 'rgba(234, 179, 8, 0.18)',
              color: '#facc15',
              border: '1px solid rgba(234, 179, 8, 0.35)',
              backdropFilter: 'blur(8px)',
              letterSpacing: '0.4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              🪙 <span>Coin</span>
            </span>
          )}
          {hasCardArt && (
            <span style={{
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '12px',
              backgroundColor: 'rgba(56, 189, 248, 0.18)',
              color: '#38bdf8',
              border: '1px solid rgba(56, 189, 248, 0.35)',
              backdropFilter: 'blur(8px)',
              letterSpacing: '0.4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              🪪 <span>Card</span>
            </span>
          )}
          {hasBlueprintMode && !hasPredefinedArt && (
            <span style={{
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '12px',
              backgroundColor: 'rgba(148, 163, 184, 0.15)',
              color: '#cbd5e1',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              backdropFilter: 'blur(8px)'
            }}>
              ✨ Custom Blueprint
            </span>
          )}
          {hasLibraryMode && !hasPredefinedArt && (
            <span style={{
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '12px',
              backgroundColor: 'rgba(46, 160, 67, 0.18)',
              color: '#3fb950',
              border: '1px solid rgba(46, 160, 67, 0.35)',
              backdropFilter: 'blur(8px)',
              letterSpacing: '0.4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              📚 <span>Operator Library</span>
            </span>
          )}
        </div>

        {/* Zoom Hint Icon in Top Right */}
        {hasPredefinedArt && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '12px',
            zIndex: 30,
            padding: '4px 8px',
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            color: '#e2e8f0',
            fontSize: '10px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backdropFilter: 'blur(8px)',
            opacity: isHovered ? 1 : 0.7,
            transition: 'opacity 0.2s'
          }}>
            <span>🔍</span> <span>Inspect</span>
          </div>
        )}

        {/* ---------------------------------------------------- */}
        {/* CASE 1: BUNDLE SHOWCASE (Both Cards and Coins)        */}
        {/* ---------------------------------------------------- */}
        {isBundle && (
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Cards in the background (Moved Down and to the Left) */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-65px, 22px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}>
              {/* Back Card (Tilted Left) */}
              <div style={{
                position: 'absolute',
                width: '135px',
                height: '190px',
                borderRadius: '9px',
                border: '1.5px solid rgba(255, 255, 255, 0.22)',
                backgroundColor: '#0f172a',
                overflow: 'hidden',
                boxShadow: isHovered 
                  ? '-12px 20px 38px rgba(0,0,0,0.8)' 
                  : '-7px 14px 28px rgba(0,0,0,0.7)',
                transform: isHovered 
                  ? 'translate(-28px, -4px) rotate(-15deg) scale(0.98)' 
                  : 'translate(-28px, 0px) rotate(-12deg) scale(0.96)',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {cardBackImg ? (
                  <img src={cardBackImg} alt="Card Back" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle, #1e293b 0%, #020617 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    color: '#64748b'
                  }}>
                    <span style={{ fontSize: '26px' }}>🪪</span>
                    <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Back</span>
                  </div>
                )}
              </div>

              {/* Front Card (Card on the Right - Tilted Right) */}
              <div style={{
                position: 'absolute',
                width: '145px',
                height: '204px',
                borderRadius: '9px',
                border: '2px solid rgba(255, 255, 255, 0.35)',
                backgroundColor: '#0f172a',
                overflow: 'hidden',
                boxShadow: isHovered 
                  ? '14px 24px 44px rgba(0,0,0,0.85)' 
                  : '8px 16px 32px rgba(0,0,0,0.75)',
                transform: isHovered 
                  ? 'translate(24px, -4px) rotate(9deg) scale(1.03)' 
                  : 'translate(24px, 0px) rotate(7deg) scale(1)',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2
              }}>
                {cardFrontImg ? (
                  <img src={cardFrontImg} alt="Card Front" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle, #334155 0%, #0f172a 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '30px'
                  }}>
                    🪪
                  </div>
                )}
              </div>
            </div>

            {/* Coins in Foreground (Positioned on the Right, moved down 20%, with Left Coin overlaying Card on the Right by 20% of coin width) */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              {/* Tails Coin (Left Coin - overlays card on the right by ~20% of coin diameter) */}
              <div style={{
                position: 'absolute',
                width: '84px',
                height: '84px',
                borderRadius: '50%',
                background: coinMetal.rimGradient,
                padding: '3.5px',
                boxShadow: isHovered 
                  ? '-8px 14px 28px rgba(0,0,0,0.92), 0 0 16px ' + coinMetal.glowColor 
                  : '-5px 10px 22px rgba(0,0,0,0.85)',
                transform: isHovered 
                  ? 'translate(59px, -4px) rotate(-8deg) scale(1.04)' 
                  : 'translate(59px, 0px) rotate(-5deg) scale(1)',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                boxSizing: 'border-box',
                zIndex: 10
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  backgroundColor: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  {tailsImg ? (
                    <img src={tailsImg} alt="Tails" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#facc15' }}>TAILS</span>
                  )}
                  {/* Subtle Metallic Sheen */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: coinMetal.sheen,
                    pointerEvents: 'none'
                  }} />
                </div>
              </div>

              {/* Heads Coin (Right Coin - shifted further right and overlapping left coin) */}
              <div style={{
                position: 'absolute',
                width: '92px',
                height: '92px',
                borderRadius: '50%',
                background: coinMetal.rimGradient,
                padding: '4px',
                boxShadow: isHovered 
                  ? '10px 20px 36px rgba(0,0,0,0.95), 0 0 20px ' + coinMetal.glowColor 
                  : '6px 14px 26px rgba(0,0,0,0.9)',
                transform: isHovered 
                  ? 'translate(114px, 8px) rotate(10deg) scale(1.06)' 
                  : 'translate(114px, 12px) rotate(6deg) scale(1.02)',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                boxSizing: 'border-box',
                zIndex: 12
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  backgroundColor: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  {headsImg ? (
                    <img src={headsImg} alt="Heads" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#facc15' }}>HEADS</span>
                  )}
                  {/* Subtle Metallic Sheen */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: coinMetal.sheen,
                    pointerEvents: 'none'
                  }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ---------------------------------------------------- */}
        {/* CASE 2: CARD FAN SHOWCASE ONLY                       */}
        {/* ---------------------------------------------------- */}
        {!isBundle && hasCardArt && (
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Back Card (Tilted Left) */}
            <div style={{
              position: 'absolute',
              width: '150px',
              height: '210px',
              borderRadius: '9px',
              border: '1.5px solid rgba(255, 255, 255, 0.22)',
              backgroundColor: '#0f172a',
              overflow: 'hidden',
              boxShadow: isHovered 
                ? '-14px 22px 42px rgba(0,0,0,0.8)' 
                : '-8px 14px 30px rgba(0,0,0,0.7)',
              transform: isHovered 
                ? 'rotate(-16deg) translate(-42px, 6px) scale(0.98)' 
                : 'rotate(-11deg) translate(-32px, 4px) scale(0.96)',
              transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}>
              {cardBackImg ? (
                <img src={cardBackImg} alt="Card Back" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle, #1e293b 0%, #020617 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  color: '#64748b'
                }}>
                  <span style={{ fontSize: '26px' }}>🪪</span>
                  <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Back Face</span>
                </div>
              )}
            </div>

            {/* Front Card (Tilted Right, Overlapping) */}
            <div style={{
              position: 'absolute',
              width: '160px',
              height: '225px',
              borderRadius: '9px',
              border: '2px solid rgba(255, 255, 255, 0.35)',
              backgroundColor: '#0f172a',
              overflow: 'hidden',
              boxShadow: isHovered 
                ? '16px 26px 48px rgba(0,0,0,0.85)' 
                : '10px 18px 36px rgba(0,0,0,0.78)',
              transform: isHovered 
                ? 'rotate(10deg) translate(36px, -10px) scale(1.05)' 
                : 'rotate(7deg) translate(28px, -4px) scale(1.02)',
              transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}>
              {cardFrontImg ? (
                <img src={cardFrontImg} alt="Card Front" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle, #334155 0%, #0f172a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px'
                }}>
                  🪪
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------------------------------------------------- */}
        {/* CASE 3: OVERLAPPED COINS SHOWCASE ONLY               */}
        {/* ---------------------------------------------------- */}
        {!isBundle && hasCoinArt && (
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Tails Coin (Face 2 - Left) */}
            <div style={{
              position: 'absolute',
              width: '110px',
              height: '110px',
              borderRadius: '50%',
              background: coinMetal.rimGradient,
              padding: '4.5px',
              boxShadow: isHovered 
                ? '-14px 20px 38px rgba(0,0,0,0.9), 0 0 16px ' + coinMetal.glowColor 
                : '-8px 12px 28px rgba(0,0,0,0.8)',
              transform: isHovered 
                ? 'rotate(-14deg) translate(-42px, 6px) scale(1)' 
                : 'rotate(-9deg) translate(-34px, 2px) scale(0.97)',
              transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
              boxSizing: 'border-box',
              zIndex: 1
            }}>
              <div style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: '#0f172a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                {tailsImg ? (
                  <img src={tailsImg} alt="Tails" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ textAlign: 'center', color: '#facc15' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800 }}>TAILS</div>
                    <div style={{ fontSize: '9px', opacity: 0.8 }}>FACE 2</div>
                  </div>
                )}
                {/* Metallic Sheen */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: coinMetal.sheen,
                  pointerEvents: 'none'
                }} />
              </div>
            </div>

            {/* Heads Coin (Face 1 - Right, Overlapping) */}
            <div style={{
              position: 'absolute',
              width: '122px',
              height: '122px',
              borderRadius: '50%',
              background: coinMetal.rimGradient,
              padding: '5px',
              boxShadow: isHovered 
                ? '14px 24px 44px rgba(0,0,0,0.95), 0 0 22px ' + coinMetal.glowColor 
                : '9px 16px 34px rgba(0,0,0,0.88)',
              transform: isHovered 
                ? 'rotate(10deg) translate(36px, -8px) scale(1.05)' 
                : 'rotate(6deg) translate(28px, -2px) scale(1.01)',
              transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
              boxSizing: 'border-box',
              zIndex: 2
            }}>
              <div style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: '#0f172a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                {headsImg ? (
                  <img src={headsImg} alt="Heads" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ textAlign: 'center', color: '#facc15' }}>
                    <div style={{ fontSize: '14px', fontWeight: 800 }}>HEADS</div>
                    <div style={{ fontSize: '9px', opacity: 0.8 }}>FACE 1</div>
                  </div>
                )}
                {/* Metallic Sheen */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: coinMetal.sheen,
                  pointerEvents: 'none'
                }} />
              </div>
            </div>
          </div>
        )}

        {/* ---------------------------------------------------- */}
        {/* CASE 4: BLUEPRINT WIREFRAME (Blank Coin / Card)      */}
        {/* ---------------------------------------------------- */}
        {!hasPredefinedArt && hasBlueprintMode && (
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {isBlankCoin && (
                <div style={{
                  width: '68px',
                  height: '68px',
                  borderRadius: '50%',
                  border: '2px dashed rgba(234, 179, 8, 0.45)',
                  backgroundColor: 'rgba(234, 179, 8, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '26px'
                }}>
                  🪙
                </div>
              )}
              {isBlankCard && (
                <div style={{
                  width: '54px',
                  height: '76px',
                  borderRadius: '6px',
                  border: '2px dashed rgba(56, 189, 248, 0.45)',
                  backgroundColor: 'rgba(56, 189, 248, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '26px'
                }}>
                  🪪
                </div>
              )}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: 500 }}>
              Blank Proofing Canvas • Customizable in Studio
            </span>
          </div>
        )}

        {/* ---------------------------------------------------- */}
        {/* CASE 5: OPERATOR LIBRARY MODE (Selected on Purchase) */}
        {/* ---------------------------------------------------- */}
        {!hasPredefinedArt && !hasBlueprintMode && hasLibraryMode && (
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {isLibraryCoin && (
                <div style={{
                  width: '68px',
                  height: '68px',
                  borderRadius: '50%',
                  border: '2px solid rgba(63, 185, 80, 0.6)',
                  backgroundColor: 'rgba(63, 185, 80, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.3)'
                }}>
                  🪙
                </div>
              )}
              {isLibraryCard && (
                <div style={{
                  width: '54px',
                  height: '76px',
                  borderRadius: '6px',
                  border: '2px solid rgba(63, 185, 80, 0.6)',
                  backgroundColor: 'rgba(63, 185, 80, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.3)'
                }}>
                  🪪
                </div>
              )}
            </div>
            <span style={{ fontSize: '11px', color: '#3fb950', fontWeight: 600 }}>
              Operator Library • Pre-approved &bull; No Proofing
            </span>
          </div>
        )}
      </div>

      {/* ---------------------------------------------------- */}
      {/* FULL-SIZE ARTWORK INSPECTION MODAL                   */}
      {/* ---------------------------------------------------- */}
      {showInspectModal && typeof document !== 'undefined' && createPortal(
        <div
          data-testid="package-art-inspect-modal"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 99999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowInspectModal(false);
          }}
        >
          <div style={{
            background: 'var(--card-bg, #161b22)',
            border: '1px solid var(--border-color, #30363d)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '840px',
            boxShadow: '0 25px 65px rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            color: 'var(--text-main, #ffffff)'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-color, #30363d)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--panel-bg, #0d1117)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '18px' }}>🎨</span>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
                    {pkg.name} — Predefined Artwork
                  </h4>
                  <div style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)' }}>
                    High-resolution artwork bundled with this mint package
                  </div>
                </div>
              </div>
              <button
                type="button"
                data-testid="close-package-art-modal-btn"
                onClick={() => setShowInspectModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-mute, #94a3b8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '6px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              maxHeight: '75vh',
              overflowY: 'auto'
            }}>
              {/* Coin Faces Inspection */}
              {hasCoinArt && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#facc15', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🪙</span> <span>Physical Coin Faces ({sysCoin?.name || 'Custom Coin'})</span>
                    </div>
                    {sysCoin?.body_filament && (
                      <span style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)' }}>
                        Filaments: <strong>{sysCoin.body_filament}</strong> / {sysCoin.accent_filament || 'Default'}
                      </span>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '32px',
                    padding: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.06)'
                  }}>
                    {/* Heads */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '170px',
                        height: '170px',
                        borderRadius: '50%',
                        background: coinMetal.rimGradient,
                        padding: '6px',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          backgroundColor: '#0f172a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {headsImg ? (
                            <img src={headsImg} alt="Heads (Face 1)" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ fontSize: '13px', color: '#94a3b8' }}>Blank Face 1</span>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>Heads (Face 1)</span>
                    </div>

                    {/* Tails */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '170px',
                        height: '170px',
                        borderRadius: '50%',
                        background: coinMetal.rimGradient,
                        padding: '6px',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          backgroundColor: '#0f172a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {tailsImg ? (
                            <img src={tailsImg} alt="Tails (Face 2)" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ fontSize: '13px', color: '#94a3b8' }}>Blank Face 2</span>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>Tails (Face 2)</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Trophy Card Template Inspection */}
              {hasCardArt && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🪪</span> <span>Trophy Card Template ({cardDesign?.name || 'Custom Card'})</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '32px',
                    padding: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.06)'
                  }}>
                    {/* Front Face */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '190px',
                        height: '266px',
                        borderRadius: '10px',
                        border: '2px solid rgba(255, 255, 255, 0.25)',
                        backgroundColor: '#0f172a',
                        overflow: 'hidden',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {cardFrontImg ? (
                          <img src={cardFrontImg} alt="Card Front" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '13px', color: '#94a3b8' }}>No Front Art</span>
                        )}
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>Card Front</span>
                    </div>

                    {/* Back Face */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '190px',
                        height: '266px',
                        borderRadius: '10px',
                        border: '2px solid rgba(255, 255, 255, 0.25)',
                        backgroundColor: '#0f172a',
                        overflow: 'hidden',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {cardBackImg ? (
                          <img src={cardBackImg} alt="Card Back" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '13px', color: '#94a3b8' }}>Standard Back</span>
                        )}
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>Card Back</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--border-color, #30363d)',
              background: 'var(--panel-bg, #0d1117)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={() => setShowInspectModal(false)}
                style={{
                  padding: '8px 18px',
                  borderRadius: '8px',
                  background: 'var(--primary-gradient, var(--primary-color, #38bdf8))',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '13px',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
